import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import type {
  BulkRescheduleRehearsalSessionsDTO,
  BulkUpdateRehearsalSessionStatusDTO,
  ConvertRequestToEventDTO,
  CreateAssignmentDTO,
  AddEventShowToAllAssignmentsDTO,
  BulkUpdateAssignmentShowsDTO,
  CreateBankAccountTemplateDTO,
  CreateCommissionDTO,
  BulkCreateEventOutsourcesFromStaffDTO,
  BulkCreateOutsourcesFromStaffDTO,
  CreateEventOutsourceDTO,
  CreateOutsourceCategoryDTO,
  CreateOutsourceDTO,
  CreateOutsourcePayoutDTO,
  SaveEventOutsourceInstallmentPlanDTO,
  CreateProductionCostLineDTO,
  AssignEventStaffDTO,
  CreateDocumentFromParentDTO,
  CreateEventCategoryDTO,
  CreateEventDTO,
  CreateEventShowDTO,
  EventCalendarQueryDTO,
  EventDateConflictQueryDTO,
  RequestCalendarQueryDTO,
  CreateInstallmentPlanTemplateDTO,
  CreateOutsourceApInstallmentPlanTemplateDTO,
  UpdateOutsourceApInstallmentPlanTemplateDTO,
  CreateManualLineDTO,
  CreatePaymentReceiptDTO,
  CreatePaymentTermsTemplateDTO,
  CreateStaffDTO,
  CreateProductionPayoutDTO,
  CreateStaffPayoutDTO,
  CreatePerDiemPolicyDTO,
  CreatePerformerDTO,
  CreateRehearsalSessionDTO,
  CreateRequestDTO,
  MarkPayoutPaidDTO,
  PostponeEventDTO,
  RoleFeeKind,
  SetPayoutPaymentProofDTO,
  UpdateAssignmentDTO,
  UpdateBankAccountTemplateDTO,
  UpdateCommissionDTO,
  UpdateProductionCostLineDTO,
  UpdateDocumentBankAccountDTO,
  UpdateDocumentPaymentTermsDTO,
  UpdateDocumentTaxDTO,
  UpdateDocumentBillDiscountDTO,
  UpdateEventCategoryDTO,
  UpdateEventDTO,
  UpdateEventFxRatesDTO,
  UpdateEventOutsourceDTO,
  UpdateEventStaffAssignmentDTO,
  UpdateOutsourceCategoryDTO,
  UpdateOutsourceDTO,
  UpdateEventShowDTO,
  UpdateInstallmentPlanDTO,
  UpdateInstallmentPlanTemplateDTO,
  UpdateManualLineDTO,
  UpdatePaymentReceiptDraftDTO,
  UpdatePaymentTermsTemplateDTO,
  UpdatePayoutDTO,
  UpdatePerDiemPolicyDTO,
  UpdatePerformerDTO,
  UpdateStaffDTO,
  UpdateRehearsalSessionDTO,
  UpdateRequestDTO,
  UpdateRoleFeeConfigDTO,
} from "../../domain/contracts";
import type {
  PerformanceEventReportQueryDTO,
  PerformancePayoutReportQueryDTO,
  PerformancePerformerReportQueryDTO,
  PerformanceReportSummaryQueryDTO,
  PerformanceRequestReportQueryDTO,
  PerformanceRevenueReportQueryDTO,
} from "../../domain/contracts/reports";
import { performanceApi } from "./client";

// ─── Query Keys ────────────────────────────────────────────────────────────────

export const performersKeys = {
  all: ["performers"] as const,
  lists: () => [...performersKeys.all, "list"] as const,
  list: (q: OffsetPageQueryDTO) => [...performersKeys.lists(), q] as const,
  details: () => [...performersKeys.all, "detail"] as const,
  detail: (id: string) => [...performersKeys.details(), id] as const,
};

export const staffKeys = {
  all: ["performance-staff"] as const,
  lists: () => [...staffKeys.all, "list"] as const,
  list: (q: { q?: string; limit: number; skip: number }) =>
    [...staffKeys.lists(), q] as const,
  details: () => [...staffKeys.all, "detail"] as const,
  detail: (id: string) => [...staffKeys.details(), id] as const,
};

export const outsourcesKeys = {
  all: ["performance-outsources"] as const,
  lists: () => [...outsourcesKeys.all, "list"] as const,
  list: (q: {
    q?: string;
    categoryId?: string;
    payeeType?: "individual" | "team";
    status?: "active" | "inactive";
    limit: number;
    skip: number;
  }) => [...outsourcesKeys.lists(), q] as const,
  details: () => [...outsourcesKeys.all, "detail"] as const,
  detail: (id: string) => [...outsourcesKeys.details(), id] as const,
};

export const outsourceCategoriesKeys = {
  all: ["performance-outsource-categories"] as const,
  list: () => [...outsourceCategoriesKeys.all, "list"] as const,
};

export const paymentTermsTemplatesKeys = {
  all: ["performance-payment-terms-templates"] as const,
  lists: () => [...paymentTermsTemplatesKeys.all, "list"] as const,
  list: (q: OffsetPageQueryDTO) =>
    [...paymentTermsTemplatesKeys.lists(), q] as const,
  lookup: (
    documentType?: "quotation" | "invoice",
    taxVariant?: "without_tax" | "with_tax",
  ) =>
    [
      ...paymentTermsTemplatesKeys.all,
      "lookup",
      documentType ?? "all",
      taxVariant ?? "any",
    ] as const,
  details: () => [...paymentTermsTemplatesKeys.all, "detail"] as const,
  detail: (id: string) => [...paymentTermsTemplatesKeys.details(), id] as const,
};

export const bankAccountTemplatesKeys = {
  all: ["performance-bank-account-templates"] as const,
  lists: () => [...bankAccountTemplatesKeys.all, "list"] as const,
  list: (q: OffsetPageQueryDTO) =>
    [...bankAccountTemplatesKeys.lists(), q] as const,
  lookup: (taxVariant?: "without_tax" | "with_tax") =>
    [...bankAccountTemplatesKeys.all, "lookup", taxVariant ?? "any"] as const,
  details: () => [...bankAccountTemplatesKeys.all, "detail"] as const,
  detail: (id: string) => [...bankAccountTemplatesKeys.details(), id] as const,
};

export const outsourceApInstallmentPlanTemplatesKeys = {
  all: ["performance-outsource-ap-installment-plan-templates"] as const,
  lists: () => [...outsourceApInstallmentPlanTemplatesKeys.all, "list"] as const,
  list: (q: OffsetPageQueryDTO) =>
    [...outsourceApInstallmentPlanTemplatesKeys.lists(), q] as const,
  lookup: () =>
    [...outsourceApInstallmentPlanTemplatesKeys.all, "lookup"] as const,
  details: () =>
    [...outsourceApInstallmentPlanTemplatesKeys.all, "detail"] as const,
  detail: (id: string) =>
    [...outsourceApInstallmentPlanTemplatesKeys.details(), id] as const,
};

export const installmentPlanTemplatesKeys = {
  all: ["performance-installment-plan-templates"] as const,
  lists: () => [...installmentPlanTemplatesKeys.all, "list"] as const,
  list: (q: OffsetPageQueryDTO) =>
    [...installmentPlanTemplatesKeys.lists(), q] as const,
  lookup: () => [...installmentPlanTemplatesKeys.all, "lookup"] as const,
  details: () => [...installmentPlanTemplatesKeys.all, "detail"] as const,
  detail: (id: string) =>
    [...installmentPlanTemplatesKeys.details(), id] as const,
};

export const perDiemPoliciesKeys = {
  all: ["performance-per-diem-policies"] as const,
  lists: () => [...perDiemPoliciesKeys.all, "list"] as const,
  list: (q: OffsetPageQueryDTO) => [...perDiemPoliciesKeys.lists(), q] as const,
  lookup: () => [...perDiemPoliciesKeys.all, "lookup"] as const,
  details: () => [...perDiemPoliciesKeys.all, "detail"] as const,
  detail: (id: string) => [...perDiemPoliciesKeys.details(), id] as const,
};

export const eventCategoriesKeys = {
  all: ["performance-event-categories"] as const,
  list: () => [...eventCategoriesKeys.all, "list"] as const,
  lookup: (q?: string) => [...eventCategoriesKeys.all, "lookup", q ?? ""] as const,
  details: () => [...eventCategoriesKeys.all, "detail"] as const,
  detail: (id: string) => [...eventCategoriesKeys.details(), id] as const,
};

export const roleFeeConfigsKeys = {
  all: ["performance-role-fee-configs"] as const,
  list: () => [...roleFeeConfigsKeys.all, "list"] as const,
};

export const eventsKeys = {
  all: ["performance-events"] as const,
  lists: () => [...eventsKeys.all, "list"] as const,
  list: (q: OffsetPageQueryDTO) => [...eventsKeys.lists(), q] as const,
  calendars: () => [...eventsKeys.all, "calendar"] as const,
  calendar: (q: EventCalendarQueryDTO) =>
    [...eventsKeys.calendars(), q] as const,
  dateConflicts: (q: EventDateConflictQueryDTO) =>
    [...eventsKeys.all, "date-conflicts", q] as const,
  teamAssignmentWarnings: () =>
    [...eventsKeys.all, "team-assignment-warnings", "count"] as const,
  details: () => [...eventsKeys.all, "detail"] as const,
  detail: (id: string) => [...eventsKeys.details(), id] as const,
  calendarSync: (id: string) =>
    [...eventsKeys.detail(id), "google-calendar"] as const,
  report: (eventId: string) =>
    [...eventsKeys.detail(eventId), "report"] as const,
  editForm: (id: string) => [...eventsKeys.details(), id, "edit-form"] as const,
  dateChanges: (id: string) =>
    [...eventsKeys.detail(id), "date-changes"] as const,
  assignments: (eventId: string) =>
    [...eventsKeys.detail(eventId), "assignments"] as const,
  rehearsalSummary: (eventId: string) =>
    [...eventsKeys.detail(eventId), "rehearsal-summary"] as const,
  rehearsalSessions: (eventId: string) =>
    [...eventsKeys.detail(eventId), "rehearsal-sessions"] as const,
  shows: (eventId: string) =>
    [...eventsKeys.detail(eventId), "shows"] as const,
  documents: (eventId: string) =>
    [...eventsKeys.detail(eventId), "documents"] as const,
  paymentSummary: (eventId: string, invoiceId: string) =>
    [...eventsKeys.detail(eventId), "payment-summary", invoiceId] as const,
  quotationInstallmentPlan: (eventId: string, quotationId: string) =>
    [
      ...eventsKeys.detail(eventId),
      "quotation-installment-plan",
      quotationId,
    ] as const,
  payouts: (eventId: string) =>
    [...eventsKeys.detail(eventId), "payouts"] as const,
  commissions: (eventId: string) =>
    [...eventsKeys.detail(eventId), "commissions"] as const,
  productionCosts: (eventId: string) =>
    [...eventsKeys.detail(eventId), "production-costs"] as const,
  outsources: (eventId: string) =>
    [...eventsKeys.detail(eventId), "outsources"] as const,
  staff: (eventId: string) => [...eventsKeys.detail(eventId), "staff"] as const,
};

export const documentsKeys = {
  all: ["performance-documents"] as const,
  lists: () => [...documentsKeys.all, "list"] as const,
  list: () => [...documentsKeys.lists()] as const,
  details: () => [...documentsKeys.all, "detail"] as const,
  detail: (documentId: string) =>
    [...documentsKeys.details(), documentId] as const,
};

export const payoutsKeys = {
  all: ["performance-payouts"] as const,
  lists: () => [...payoutsKeys.all, "list"] as const,
  list: () => [...payoutsKeys.lists()] as const,
  details: () => [...payoutsKeys.all, "detail"] as const,
  detail: (payoutId: string) => [...payoutsKeys.details(), payoutId] as const,
  pricingInsights: (payoutId: string) =>
    [...payoutsKeys.all, "pricing-insights", payoutId] as const,
};

export const requestsKeys = {
  all: ["performance-requests"] as const,
  lists: () => [...requestsKeys.all, "list"] as const,
  list: (q: OffsetPageQueryDTO) => [...requestsKeys.lists(), q] as const,
  calendars: () => [...requestsKeys.all, "calendar"] as const,
  calendar: (q: RequestCalendarQueryDTO) =>
    [...requestsKeys.calendars(), q] as const,
  details: () => [...requestsKeys.all, "detail"] as const,
  detail: (id: string) => [...requestsKeys.details(), id] as const,
  calendarSync: (id: string) =>
    [...requestsKeys.detail(id), "google-calendar"] as const,
};

const PERFORMANCE_ERROR_MESSAGES: Record<string, string> = {
  CHILD_DOCUMENT_EXISTS: "ມີເອກະສານຕໍ່ເນື່ອງທີ່ຍັງໃຊ້ງານຢູ່ແລ້ວ",
  SYNC_DISABLED: "ຍັງບໍ່ເປີດການຊິງຄ໌ Google Calendar",
  INVOICE_NOT_PAYABLE: "ໃບເກັບເງິນນີ້ຍັງຮັບຊຳລະບໍ່ໄດ້",
  INVOICE_HAS_NO_REMAINING: "ຍອດຄ້າງເປັນ 0 ແລ້ວ",
  PAYMENT_AMOUNT_EXCEEDS_REMAINING: "ຍອດຮັບເກີນຍອດຄ້າງຊຳລະ",
  INSTALLMENT_NOT_PENDING: "ງວດນີ້ຖືກປິດແລ້ວ ຫຼື ບໍ່ສາມາດແກ້ໄຂໄດ້",
  INSTALLMENT_AMOUNT_MISMATCH: "ຜົນລວມແຜນງວດບໍ່ເທົ່າຍອດໃບເກັບເງິນ",
  QUOTATION_INSTALLMENT_REQUIRED: "ກະລຸນາເລືອກງວດກ່ອນສ້າງໃບເກັບເງິນ",
  QUOTATION_INSTALLMENT_PLAN_REQUIRED: "ກະນົດແຜນງວດໃນໃບສະເໜີລາຄາກ່ອນ",
  INSTALLMENT_ALREADY_INVOICED: "ງວດນີ້ສ້າງໃບເກັບເງິນແລ້ວ",
  INSTALLMENT_NOT_FOUND: "ບໍ່ພົບງວດທີ່ເລືອກ",
  INSTALLMENT_PLAN_MANAGED_ON_QUOTATION:
    "ແຜນງວດຈັດການໃນໃບສະເໜີລາຄາ — ບໍ່ແກ້ໄຂໃນໃບເກັບເງິນ",
  USE_PAYMENT_RECEIPT_API: "ໃຊ້ການຮັບຊຳລະແບບງວດແທນ",
  DOCUMENT_HAS_ISSUED_CHILD: "ຍົກເລີກບໍ່ໄດ້ ເພາະມີເອກະສານລູກທີ່ອອກແລ້ວ",
  DOCUMENT_HAS_ACTIVE_CHILD: "ລຶບບໍ່ໄດ້ ເພາະມີເອກະສານລູກທີ່ຍັງໃຊ້ງານຢູ່",
  DOCUMENT_NOT_DELETABLE: "ລຶບໄດ້ສະເພາະເອກະສານທີ່ຍົກເລີກແລ້ວ",
  DOCUMENT_NOT_EDITABLE: "ເອກະສານນີ້ຖືກລັອກແລ້ວ ແກ້ໄຂບໍ່ໄດ້",
  DOCUMENT_NOT_ISSUABLE: "ສະຖານະເອກະສານນີ້ຍັງອອກເອກະສານບໍ່ໄດ້",
  DOCUMENT_HAS_PAYMENTS: "ຍ້ອນກັບບໍ່ໄດ້ ເພາະມີການຊຳລະເງິນແລ້ວ",
  DOCUMENT_NOT_REOPENABLE:
    "ຍ້ອນກັບເປັນຮ່າງໄດ້ສະເພາະເອກະສານທີ່ອອກແລ້ວ (issued) ເທົ່ານັ້ນ",
  INVALID_PARENT_DOCUMENT_TYPE: "ປະເພດເອກະສານແມ່ບໍ່ຖືກຕ້ອງ",
  PARENT_DOCUMENT_NOT_ISSUED: "ຕ້ອງອອກເອກະສານແມ່ກ່ອນ",
  PAYOUT_ALREADY_EXISTS: "ມີໃບເບີກຈ່າຍຂອງງານນີ້ແລ້ວ",
  PAYOUT_LEGACY_EXISTS: "ມີໃບເບີກຈ່າຍແບບເກົ່າ (ຕໍ່ຄົນ) ຄ້າງຢູ່ — ຍົກເລີກກ່ອນສ້າງໃບລວມ",
  NO_ASSIGNMENTS_FOR_EVENT: "ສ້າງໃບເບີກຈ່າຍບໍ່ໄດ້ ເພາະບໍ່ມີນັກສະແດງໃນງານນີ້",
  NO_ASSIGNMENTS_FOR_PERFORMER: "ສ້າງໃບເບີກຈ່າຍບໍ່ໄດ້ ເພາະບໍ່ມີ assignment ຂອງນັກສະແດງນີ້",
  PAYOUT_NOT_EDITABLE: "ແກ້ໄຂໄດ້ສະເພາະໃບເບີກຈ່າຍສະຖານະຮ່າງ",
  PAYOUT_NOT_SUBMITTABLE: "ສົ່ງອະນຸມັດໄດ້ສະເພາະສະຖານະຮ່າງ",
  PAYOUT_HAS_NO_LINES: "ຕ້ອງມີລາຍການຢ່າງນ້ອຍ 1 ລາຍການ",
  PAYOUT_NOT_APPROVABLE: "ອະນຸມັດໄດ້ສະເພາະໃບທີ່ລໍຖ້າອະນຸມັດ",
  PAYOUT_NOT_REJECTABLE: "ສົ່ງກັບໄດ້ສະເພາະໃບທີ່ລໍຖ້າອະນຸມັດ",
  PAYOUT_NOT_PAYABLE: "ບັນທຶກຈ່າຍໄດ້ສະເພາະໃບທີ່ອະນຸມັດແລ້ວ",
  PAYOUT_PAYMENT_PROOF_NOT_ALLOWED: "ແນບຫຼັກຖານການຈ່າຍໄດ້ສະເພາະໃບທີ່ອະນຸມັດແລ້ວ",
  INVALID_PAYMENT_PROOF_KEY: "ໄຟລ໌ຫຼັກຖານການຈ່າຍບໍ່ຖືກຕ້ອງ",
  PAYOUT_ALREADY_PAID: "ໃບນີ້ຈ່າຍແລ້ວ ຍົກເລີກບໍ່ໄດ້",
  PAYOUT_NOT_DELETABLE: "ລຶບໄດ້ສະເພາະໃບເບີກຈ່າຍທີ່ຍົກເລີກແລ້ວ",
  PAYOUT_NOT_PRINTABLE: "ພິມໃບເບີກຈ່າຍໄດ້ຫຼັງອະນຸມັດແລ້ວເທົ່ານັ້ນ",
  COMMISSION_NOT_EDITABLE: "ແກ້ໄຂຄ່ານາຍໜ້າໄດ້ສະເພາະລາຍການທີ່ລໍຖ້າຈ່າຍ",
  COMMISSION_CANCELLED: "ລາຍການນີ້ຖືກຍົກເລີກແລ້ວ",
  COMMISSION_ALREADY_PAID: "ຈ່າຍແລ້ວ ຍົກເລີກບໍ່ໄດ້",
  COMMISSION_NOT_DELETABLE: "ລຶບລາຍການທີ່ຈ່າຍແລ້ວບໍ່ໄດ້",
  COMMISSION_NOT_PRINTABLE: "ພິມໃບເບີກຈ່າຍຄ່ານາຍໜ້າໄດ້ຫຼັງຈ່າຍແລ້ວເທົ່ານັ້ນ",
  PAYOUT_EVENT_NOT_READY:
    "ຍັງສ້າງໃບເບີກຈ່າຍ Staff ບໍ່ໄດ້ — ຢືນຢັນງານ ຫຼື ອອກໃບສະເໜີລາຄາ/ໃບເກັບເງິນກ່ອນ",
  EVENT_MANUAL_BILLING_NO_ASSIGNMENTS: "ງານ Event ບໍ່ຮອງຮັບການຜູກນັກສະແດງ",
  EVENT_MANUAL_BILLING_NO_PAYOUTS: "ງານ Event ບໍ່ຮອງຮັບໃບເບີກຈ່າຍນັກສະແດງ",
  EVENT_PERFORMANCE_BILLING_NO_STAFF_PAYOUTS:
    "ງານສະແດງບໍ່ຮອງຮັບໃບເບີກຈ່າຍ Staff",
  EVENT_PERFORMANCE_BILLING_NO_STAFF_ROSTER:
    "ງານສະແດງບໍ່ຮອງຮັບການຈັດທີມ Staff",
  PAYOUT_STAFF_ALREADY_EXISTS: "ມີໃບເບີກຈ່າຍ Staff ຂອງງານນີ້ແລ້ວ",
  EVENT_STAFF_NOT_FOUND: "ບໍ່ພົບ Staff ໃນງານນີ້",
  EVENT_STAFF_INACTIVE: "Staff ນີ້ຖືກປິດໃຊ້ງານແລ້ວ",
  PAYOUT_RECIPIENT_DUPLICATE_STAFF: "ເລືອກ Staff ຊ້ຳໃນໃບດຽວກັນບໍ່ໄດ້",
  EVENT_BILLING_MODE_LOCKED: "ບໍ່ສາມາດປ່ຽນໂຫມດການຄິດລາຄາຫຼັງມີທີມ ຫຼື ເອກະສານແລ້ວ",
  EVENT_FEATURE_LOCKED:
    "ບໍ່ສາມາດປິດ feature ນີ້ — ມີຂໍ້ມູນທີ່ກ່ຽວຂ້ອງແລ້ວ (ທີມ, ເອກະສານ, ຫຼື ເບີກຈ່າຍ)",
  EVENT_FEATURE_DEPENDENCY:
    "ວັນຊ້ອມຕ້ອງເປີດ feature ນັກສະແດງກ່ອນ",
  EVENT_FEATURE_OUTSOURCES_DISABLED: "ປິດ feature Outsource ໃນງານນີ້",
  EVENT_OUTSOURCE_NOT_PLANNED: "ແກ້ໄຂ/ສ້າງ payout ໄດ້ສະເພາະແຖວສະຖານະ «ວາງແຜນ»",
  PAYOUT_OUTSOURCE_ALREADY_EXISTS: "ມີໃບເບີກຈ່າຍ Outsource ສຳລັບແຖວນີ້ແລ້ວ",
  OUTSOURCE_CATEGORY_NOT_FOUND: "ບ໵່ພົບຫມວດ Outsource",
  EVENT_CATEGORY_SLUG_EXISTS: "slug ນີ້ມີແລ້ວ",
  ROLE_FEE_CONFIG_NOT_FOUND: "ບໍ່ພົບການຕັ້ງຄ່າຄ່າຕົວສຳລັບບົດບາດນີ້",
  SOURCE_EVENT_NOT_FOUND: "ບໍ່ພົບງານສະແດງຕົ້ນແບບ",
  ROOM_BOOKING_CONFLICT: "ຫ້ອງຖືກຈອງໃນເວລານີ້ແລ້ວ",
  EVENT_DATE_UNCHANGED:
    "ວັນໃໝ່ຕ້ອງຕ່າງຈາກວັນປັດຈຸບັນ (ຢ່າງນ້ອຍຕ້ອງປ່ຽນວັນປະຕິທິນ - ປ່ຽນແຕ່ເວລາບໍ່ພໍ)",
  EVENT_NOT_POSTPONABLE:
    "ເລື່ອນວັນໄດ້ສະເພາະ ງານສະຖານະຮ່າງ ຫຼື ຢືນຢັນແລ້ວ",
  INVALID_REASON: "ຕ້ອງປ້ອນເຫດຜົນການເລື່ອນ",
  INVALID_EVENT_DATE: "ວັນທີໃໝ່ບໍ່ຖືກຕ້ອງ",
};

function getPerformanceErrorMessage(error: unknown, fallback: string) {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return PERFORMANCE_ERROR_MESSAGES[message] ?? fallback;
}

export function useRequestsQuery(query: OffsetPageQueryDTO) {
  return useQuery({
    queryKey: requestsKeys.list(query),
    queryFn: () => performanceApi.requests.list(query),
  });
}

export function useRequestsCalendarQuery(
  query: RequestCalendarQueryDTO,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: requestsKeys.calendar(query),
    queryFn: () => performanceApi.requests.getCalendar(query),
    enabled: options?.enabled ?? true,
  });
}

export function useRequestQuery(id: string, enabled = true) {
  return useQuery({
    queryKey: requestsKeys.detail(id),
    queryFn: () => performanceApi.requests.get(id),
    enabled: !!id && enabled,
  });
}

export function useRequestCalendarSyncQuery(id: string, enabled = true) {
  return useQuery({
    queryKey: requestsKeys.calendarSync(id),
    queryFn: () => performanceApi.requests.googleCalendar(id),
    enabled: !!id && enabled,
  });
}

export function useResyncRequestCalendar(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => performanceApi.requests.syncGoogleCalendar(id),
    onSuccess: () => {
      toast.success("ສົ່ງຊິງຄ໌ Google Calendar ແລ້ວ");
      void qc.invalidateQueries({ queryKey: requestsKeys.calendarSync(id) });
    },
    onError: (error) => {
      toast.error(
        getPerformanceErrorMessage(error, "ຊິງຄ໌ Google Calendar ບໍ່ສຳເລັດ"),
      );
    },
  });
}

export function useCreateRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRequestDTO) =>
      performanceApi.requests.create(input),
    onSuccess: () => {
      toast.success("ສ້າງຄຳຂໍສຳເລັດ");
      qc.invalidateQueries({ queryKey: requestsKeys.all });
    },
    onError: () => {
      toast.error("ສ້າງຄຳຂໍບໍ່ສຳເລັດ");
    },
  });
}

export function useUpdateRequest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateRequestDTO) =>
      performanceApi.requests.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດຄຳຂໍສຳເລັດ");
      // Do not await list refetch (progress calc) while save button is pending.
      void qc.invalidateQueries({ queryKey: requestsKeys.all });
    },
    onError: () => {
      toast.error("ອັບເດດຄຳຂໍບໍ່ສຳເລັດ");
    },
  });
}

export function useDeleteRequest() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => performanceApi.requests.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: requestsKeys.all }),
    onError: () => toast.error("ລຶບຄຳຂໍບໍ່ສຳເລັດ"),
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

export function useConvertRequestToEvent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ConvertRequestToEventDTO) =>
      performanceApi.requests.convertToEvent(id, input),
    onSuccess: (result) => {
      const copied = result.copiedAssignments ?? 0;
      toast.success(
        copied > 0
          ? `ແປງຄຳຂໍສຳເລັດ — ຄັດລອກ ${copied} ການຈັດນັກສະແດງ`
          : "ແປງຄຳຂໍເປັນງານສະແດງສຳເລັດ",
      );
      qc.invalidateQueries({ queryKey: requestsKeys.all });
      qc.invalidateQueries({ queryKey: eventsKeys.all });
    },
    onError: () => {
      toast.error("ແປງຄຳຂໍບໍ່ສຳເລັດ");
    },
  });
}

// ─── Performers ────────────────────────────────────────────────────────────────

export function usePerformersQuery(query: OffsetPageQueryDTO) {
  return useQuery({
    queryKey: performersKeys.list(query),
    queryFn: () => performanceApi.performers.list(query),
  });
}

export function usePerformerQuery(id: string) {
  return useQuery({
    queryKey: performersKeys.detail(id),
    queryFn: () => performanceApi.performers.get(id),
    enabled: !!id,
  });
}

export function useCreatePerformer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePerformerDTO) =>
      performanceApi.performers.create(input),
    onSuccess: () => {
      toast.success("ສ້າງນັກສະແດງສຳເລັດ");
      qc.invalidateQueries({ queryKey: performersKeys.all });
    },
    onError: () => {
      toast.error("ສ້າງນັກສະແດງລົ້ມເຫຼວ");
    },
  });
}

export function useUpdatePerformer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePerformerDTO) =>
      performanceApi.performers.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດນັກສະແດງສຳເລັດ");
      qc.invalidateQueries({ queryKey: performersKeys.all });
    },
    onError: () => {
      toast.error("ອັບເດດນັກສະແດງລົ້ມເຫຼວ");
    },
  });
}

export function useDeletePerformer() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => performanceApi.performers.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: performersKeys.all }),
    onError: () => toast.error("ລຶບນັກສະແດງລົ້ມເຫຼວ"),
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

// ─── Outsources (master) ──────────────────────────────────────────────────────

export function useOutsourcesListQuery(query: {
  q?: string;
  categoryId?: string;
  payeeType?: "individual" | "team";
  status?: "active" | "inactive";
  limit: number;
  skip: number;
}) {
  return useQuery({
    queryKey: outsourcesKeys.list(query),
    queryFn: () => performanceApi.outsources.list(query),
  });
}

export function useOutsourceQuery(id: string) {
  return useQuery({
    queryKey: outsourcesKeys.detail(id),
    queryFn: () => performanceApi.outsources.get(id),
    enabled: !!id,
  });
}

export function useCreateOutsource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOutsourceDTO) =>
      performanceApi.outsources.create(input),
    onSuccess: () => {
      toast.success("ສ້າງ Outsource ສຳເລັດ");
      qc.invalidateQueries({ queryKey: outsourcesKeys.all });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ສ້າງ Outsource ບໍ່ສຳເລັດ")),
  });
}

export function useBulkCreateOutsourcesFromStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkCreateOutsourcesFromStaffDTO) =>
      performanceApi.outsources.bulkCreateFromStaff(input),
    onSuccess: (result) => {
      const skipped = result.skipped.length;
      toast.success(
        skipped > 0
          ? `ສ້າງ Outsource ${result.created.length} ລາຍການ (ຂ້າມ ${skipped})`
          : `ສ້າງ Outsource ${result.created.length} ລາຍການ`,
      );
      qc.invalidateQueries({ queryKey: outsourcesKeys.all });
    },
    onError: (error) =>
      toast.error(
        getPerformanceErrorMessage(error, "ສ້າງ Outsource bulk ບໍ່ສຳເລັດ"),
      ),
  });
}

export function useUpdateOutsource(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateOutsourceDTO) =>
      performanceApi.outsources.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດ Outsource ສຳເລັດ");
      qc.invalidateQueries({ queryKey: outsourcesKeys.all });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ອັົບເດດ Outsource ບໍ່ສຳເລັດ")),
  });
}

export function useOutsourceCategoriesQuery(enabled = true) {
  return useQuery({
    queryKey: outsourceCategoriesKeys.list(),
    queryFn: () => performanceApi.outsourceCategories.list(),
    enabled,
  });
}

export function useUpdateOutsourceCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: UpdateOutsourceCategoryDTO;
    }) => performanceApi.outsourceCategories.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດຫມວດ Outsource ສຳເລັດ");
      qc.invalidateQueries({ queryKey: outsourceCategoriesKeys.all });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ອັບເດດຫມວດບໍ່ສຳເລັດ")),
  });
}

// ─── Staff (master) ───────────────────────────────────────────────────────────

export function useStaffListQuery(query: {
  q?: string;
  limit: number;
  skip: number;
}) {
  return useQuery({
    queryKey: staffKeys.list(query),
    queryFn: () => performanceApi.staff.list(query),
  });
}

export function useStaffQuery(id: string) {
  return useQuery({
    queryKey: staffKeys.detail(id),
    queryFn: () => performanceApi.staff.get(id),
    enabled: !!id,
  });
}

export function useCreateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStaffDTO) => performanceApi.staff.create(input),
    onSuccess: () => {
      toast.success("ສ້າງ Staff ສຳເລັດ");
      qc.invalidateQueries({ queryKey: staffKeys.all });
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : "ສ້າງ Staff ລົ້ມເຫຼວ"),
  });
}

export function useUpdateStaff(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateStaffDTO) =>
      performanceApi.staff.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດ Staff ສຳເລັດ");
      qc.invalidateQueries({ queryKey: staffKeys.all });
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : "ອັບເດດ Staff ລົ້ມເຫຼວ"),
  });
}

export function useDeleteStaff() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => performanceApi.staff.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: staffKeys.all }),
    onError: () => toast.error("ລຶບ Staff ລົ້ມເຫຼວ"),
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

// ─── Per Diem Policies ────────────────────────────────────────────────────────

export function usePerDiemPoliciesQuery(query: OffsetPageQueryDTO) {
  return useQuery({
    queryKey: perDiemPoliciesKeys.list(query),
    queryFn: () => performanceApi.perDiemPolicies.list(query),
  });
}

export function usePerDiemPoliciesLookup() {
  return useQuery({
    queryKey: perDiemPoliciesKeys.lookup(),
    queryFn: () => performanceApi.perDiemPolicies.lookup(),
  });
}

export function usePerDiemPolicyQuery(id: string) {
  return useQuery({
    queryKey: perDiemPoliciesKeys.detail(id),
    queryFn: () => performanceApi.perDiemPolicies.get(id),
    enabled: !!id,
  });
}

export function useCreatePerDiemPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePerDiemPolicyDTO) =>
      performanceApi.perDiemPolicies.create(input),
    onSuccess: () => {
      toast.success("ສ້າງນະໂຍບາຍສຳເລັດ");
      qc.invalidateQueries({ queryKey: perDiemPoliciesKeys.all });
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ສ້າງນະໂຍບາຍລົ້ມເຫຼວ"));
    },
  });
}

export function useUpdatePerDiemPolicy(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePerDiemPolicyDTO) =>
      performanceApi.perDiemPolicies.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດນະໂຍບາຍສຳເລັດ");
      qc.invalidateQueries({ queryKey: perDiemPoliciesKeys.all });
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ອັບເດດນະໂຍບາຍລົ້ມເຫຼວ"));
    },
  });
}

export function useDeletePerDiemPolicy() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => performanceApi.perDiemPolicies.remove(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: perDiemPoliciesKeys.all }),
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ລຶບນະໂຍບາຍລົ້ມເຫຼວ")),
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

// ─── Event Categories ─────────────────────────────────────────────────────────

export function useEventCategoriesQuery() {
  return useQuery({
    queryKey: eventCategoriesKeys.list(),
    queryFn: () => performanceApi.eventCategories.list(),
  });
}

export function useEventCategoriesLookup(q?: string) {
  return useQuery({
    queryKey: eventCategoriesKeys.lookup(q),
    queryFn: () => performanceApi.eventCategories.lookup(q ? { q } : undefined),
  });
}

export function useEventCategoryQuery(id: string) {
  return useQuery({
    queryKey: eventCategoriesKeys.detail(id),
    queryFn: () => performanceApi.eventCategories.get(id),
    enabled: !!id,
  });
}

export function useCreateEventCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEventCategoryDTO) =>
      performanceApi.eventCategories.create(input),
    onSuccess: () => {
      toast.success("ສ້າງຫມວດງານສຳເລັດ");
      qc.invalidateQueries({ queryKey: eventCategoriesKeys.all });
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ສ້າງຫມວດງານລົ້ມເຫຼວ"));
    },
  });
}

export function useUpdateEventCategory(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateEventCategoryDTO) =>
      performanceApi.eventCategories.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດຫມວດງານສຳເລັດ");
      qc.invalidateQueries({ queryKey: eventCategoriesKeys.all });
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ອັບເດດຫມວດງານລົ້ມເຫຼວ"));
    },
  });
}

export function useDeleteEventCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => performanceApi.eventCategories.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: eventCategoriesKeys.all });
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ລຶບຫມວດງານລົ້ມເຫຼວ"));
    },
  });
}

// ─── Role Fee Configs ─────────────────────────────────────────────────────────

export function useRoleFeeConfigsQuery() {
  return useQuery({
    queryKey: roleFeeConfigsKeys.list(),
    queryFn: () => performanceApi.roleFeeConfigs.list(),
  });
}

export function useUpdateRoleFeeConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      roleFeeKind,
      input,
    }: {
      roleFeeKind: RoleFeeKind;
      input: UpdateRoleFeeConfigDTO;
    }) => performanceApi.roleFeeConfigs.update(roleFeeKind, input),
    onSuccess: () => {
      toast.success("ບັນທຶກຄ່າຕົວສຳເລັດ");
      qc.invalidateQueries({ queryKey: roleFeeConfigsKeys.all });
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ບັນທຶກຄ່າຕົວບໍ່ສຳເລັດ"));
    },
  });
}

// ─── Payment Terms Templates ──────────────────────────────────────────────────

export function usePaymentTermsTemplatesQuery(query: OffsetPageQueryDTO) {
  return useQuery({
    queryKey: paymentTermsTemplatesKeys.list(query),
    queryFn: () => performanceApi.paymentTermsTemplates.list(query),
  });
}

export function usePaymentTermsTemplatesLookup(
  documentType?: "quotation" | "invoice",
  taxVariant?: "without_tax" | "with_tax",
) {
  return useQuery({
    queryKey: paymentTermsTemplatesKeys.lookup(documentType, taxVariant),
    queryFn: () =>
      performanceApi.paymentTermsTemplates.lookup(documentType, taxVariant),
  });
}

export function usePaymentTermsTemplateQuery(id: string) {
  return useQuery({
    queryKey: paymentTermsTemplatesKeys.detail(id),
    queryFn: () => performanceApi.paymentTermsTemplates.get(id),
    enabled: !!id,
  });
}

export function useCreatePaymentTermsTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePaymentTermsTemplateDTO) =>
      performanceApi.paymentTermsTemplates.create(input),
    onSuccess: () => {
      toast.success("ສ້າງແມ່ແບບສຳເລັດ");
      qc.invalidateQueries({ queryKey: paymentTermsTemplatesKeys.all });
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ສ້າງແມ່ແບບລົ້ມເຫຼວ"));
    },
  });
}

export function useUpdatePaymentTermsTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePaymentTermsTemplateDTO) =>
      performanceApi.paymentTermsTemplates.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດແມ່ແບບສຳເລັດ");
      qc.invalidateQueries({ queryKey: paymentTermsTemplatesKeys.all });
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ອັບເດດແມ່ແບບລົ້ມເຫຼວ"));
    },
  });
}

export function useDeletePaymentTermsTemplate() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => performanceApi.paymentTermsTemplates.remove(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: paymentTermsTemplatesKeys.all }),
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ລຶບແມ່ແບບລົ້ມເຫຼວ")),
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

// ─── Bank Account Templates ───────────────────────────────────────────────────

export function useBankAccountTemplatesQuery(query: OffsetPageQueryDTO) {
  return useQuery({
    queryKey: bankAccountTemplatesKeys.list(query),
    queryFn: () => performanceApi.bankAccountTemplates.list(query),
  });
}

export function useBankAccountTemplatesLookup(
  taxVariant?: "without_tax" | "with_tax",
) {
  return useQuery({
    queryKey: bankAccountTemplatesKeys.lookup(taxVariant),
    queryFn: () => performanceApi.bankAccountTemplates.lookup(taxVariant),
  });
}

export function useBankAccountTemplateQuery(id: string) {
  return useQuery({
    queryKey: bankAccountTemplatesKeys.detail(id),
    queryFn: () => performanceApi.bankAccountTemplates.get(id),
    enabled: !!id,
  });
}

export function useCreateBankAccountTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBankAccountTemplateDTO) =>
      performanceApi.bankAccountTemplates.create(input),
    onSuccess: () => {
      toast.success("ສ້າງແມ່ແບບສຳເລັດ");
      qc.invalidateQueries({ queryKey: bankAccountTemplatesKeys.all });
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ສ້າງແມ່ແບບລົ້ມເຫຼວ"));
    },
  });
}

export function useUpdateBankAccountTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateBankAccountTemplateDTO) =>
      performanceApi.bankAccountTemplates.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດແມ່ແບບສຳເລັດ");
      qc.invalidateQueries({ queryKey: bankAccountTemplatesKeys.all });
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ອັບເດດແມ່ແບບລົ້ມເຫຼວ"));
    },
  });
}

export function useDeleteBankAccountTemplate() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => performanceApi.bankAccountTemplates.remove(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: bankAccountTemplatesKeys.all }),
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ລຶບແມ່ແບບລົ້ມເຫຼວ")),
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

// ─── Outsource AP Installment Plan Templates ───────────────────────────────────

export function useOutsourceApInstallmentPlanTemplatesQuery(
  query: OffsetPageQueryDTO,
) {
  return useQuery({
    queryKey: outsourceApInstallmentPlanTemplatesKeys.list(query),
    queryFn: () => performanceApi.outsourceApInstallmentPlanTemplates.list(query),
  });
}

export function useCreateOutsourceApInstallmentPlanTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOutsourceApInstallmentPlanTemplateDTO) =>
      performanceApi.outsourceApInstallmentPlanTemplates.create(input),
    onSuccess: () => {
      toast.success("ສ້າງແມ່ແບບງວດ AP ສຳເລັດ");
      qc.invalidateQueries({
        queryKey: outsourceApInstallmentPlanTemplatesKeys.all,
      });
    },
    onError: (error) =>
      toast.error(
        getPerformanceErrorMessage(error, "ສ້າງແມ່ແບບງວດ AP ລົ້ມເຫຼວ"),
      ),
  });
}

export function useOutsourceApInstallmentPlanTemplateQuery(id: string) {
  return useQuery({
    queryKey: outsourceApInstallmentPlanTemplatesKeys.detail(id),
    queryFn: () => performanceApi.outsourceApInstallmentPlanTemplates.get(id),
    enabled: !!id,
  });
}

export function useUpdateOutsourceApInstallmentPlanTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateOutsourceApInstallmentPlanTemplateDTO) =>
      performanceApi.outsourceApInstallmentPlanTemplates.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດແມ່ແບບງວດ AP ສຳເລັດ");
      qc.invalidateQueries({
        queryKey: outsourceApInstallmentPlanTemplatesKeys.all,
      });
    },
    onError: (error) =>
      toast.error(
        getPerformanceErrorMessage(error, "ອັບເດດແມ່ແບບງວດ AP ລົ້ມເຫຼວ"),
      ),
  });
}

export function useDeleteOutsourceApInstallmentPlanTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      performanceApi.outsourceApInstallmentPlanTemplates.remove(id),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: outsourceApInstallmentPlanTemplatesKeys.all,
      }),
    onError: (error) =>
      toast.error(
        getPerformanceErrorMessage(error, "ລຶບແມ່ແບບງວດ AP ລົ້ມເຫຼວ"),
      ),
  });
}

// ─── Installment Plan Templates ───────────────────────────────────────────────

export function useInstallmentPlanTemplatesQuery(query: OffsetPageQueryDTO) {
  return useQuery({
    queryKey: installmentPlanTemplatesKeys.list(query),
    queryFn: () => performanceApi.installmentPlanTemplates.list(query),
  });
}

export function useInstallmentPlanTemplatesLookup() {
  return useQuery({
    queryKey: installmentPlanTemplatesKeys.lookup(),
    queryFn: () => performanceApi.installmentPlanTemplates.lookup(),
  });
}

export function useInstallmentPlanTemplateQuery(id: string) {
  return useQuery({
    queryKey: installmentPlanTemplatesKeys.detail(id),
    queryFn: () => performanceApi.installmentPlanTemplates.get(id),
    enabled: !!id,
  });
}

export function useCreateInstallmentPlanTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInstallmentPlanTemplateDTO) =>
      performanceApi.installmentPlanTemplates.create(input),
    onSuccess: () => {
      toast.success("ສ້າງແມ່ແບບແຜນງວດສຳເລັດ");
      qc.invalidateQueries({ queryKey: installmentPlanTemplatesKeys.all });
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ສ້າງແມ່ແບບແຜນງວດລົ້ມເຫຼວ"));
    },
  });
}

export function useUpdateInstallmentPlanTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateInstallmentPlanTemplateDTO) =>
      performanceApi.installmentPlanTemplates.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດແມ່ແບບແຜນງວດສຳເລັດ");
      qc.invalidateQueries({ queryKey: installmentPlanTemplatesKeys.all });
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ອັບເດດແມ່ແບບແຜນງວດລົ້ມເຫຼວ"));
    },
  });
}

export function useDeleteInstallmentPlanTemplate() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) =>
      performanceApi.installmentPlanTemplates.remove(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: installmentPlanTemplatesKeys.all }),
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ລຶບແມ່ແບບແຜນງວດລົ້ມເຫຼວ")),
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

export function useUpdateDocumentPaymentTerms(
  eventId: string,
  documentId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateDocumentPaymentTermsDTO) =>
      performanceApi.events.documents.updatePaymentTerms(
        eventId,
        documentId,
        input,
      ),
    onSuccess: (document) => {
      toast.success("ບັນທຶກເງື່ອນໄຂການຊຳລະສຳເລັດ");
      qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
      qc.invalidateQueries({ queryKey: documentsKeys.detail(document.id) });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ບັນທຶກເງື່ອນໄຂການຊຳລະບໍ່ສຳເລັດ")),
  });
}

export function useUpdateDocumentBankAccount(
  eventId: string,
  documentId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateDocumentBankAccountDTO) =>
      performanceApi.events.documents.updateBankAccount(
        eventId,
        documentId,
        input,
      ),
    onSuccess: (document) => {
      toast.success("ບັນທຶກຂໍ້ມູນບັນຊີທະນາຄານສຳເລັດ");
      qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
      qc.invalidateQueries({ queryKey: documentsKeys.detail(document.id) });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ບັນທຶກຂໍ້ມູນບັນຊີທະນາຄານບໍ່ສຳເລັດ")),
  });
}

export function useUpdateDocumentTax(eventId: string, documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateDocumentTaxDTO) =>
      performanceApi.events.documents.updateTax(eventId, documentId, input),
    onSuccess: (document) => {
      toast.success("ບັນທຶກພາສີເອກະສານສຳເລັດ");
      qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
      qc.invalidateQueries({ queryKey: documentsKeys.detail(document.id) });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ບັນທຶກພາສີເອກະສານບໍ່ສຳເລັດ")),
  });
}

export function useUpdateDocumentBillDiscount(
  eventId: string,
  documentId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateDocumentBillDiscountDTO) =>
      performanceApi.events.documents.updateBillDiscount(
        eventId,
        documentId,
        input,
      ),
    onSuccess: (document) => {
      toast.success("ບັນທຶກສ່ວນຫຼຸດທ້າຍບິນສຳເລັດ");
      qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
      qc.invalidateQueries({ queryKey: documentsKeys.detail(document.id) });
    },
    onError: (error) =>
      toast.error(
        getPerformanceErrorMessage(error, "ບັນທຶກສ່ວນຫຼຸດທ້າຍບິນບໍ່ສຳເລັດ"),
      ),
  });
}

export const taxSettingsKeys = {
  all: ["performance-tax-settings"] as const,
};

export function useTaxSettingsQuery() {
  return useQuery({
    queryKey: taxSettingsKeys.all,
    queryFn: () => performanceApi.taxSettings.get(),
  });
}

export function useUpdateTaxSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { defaultTaxRate: number }) =>
      performanceApi.taxSettings.update(input),
    onSuccess: () => {
      toast.success("ບັນທຶກຕັ້ງຄ່າພາສີສຳເລັດ");
      qc.invalidateQueries({ queryKey: taxSettingsKeys.all });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ບັນທຶກຕັ້ງຄ່າພາສີບໍ່ສຳເລັດ")),
  });
}

// ─── Events ─────────────────────────────────────────────────────────────────────

export function useEventsQuery(query: OffsetPageQueryDTO) {
  return useQuery({
    queryKey: eventsKeys.list(query),
    queryFn: () => performanceApi.events.list(query),
  });
}

export function useEventsCalendarQuery(
  query: EventCalendarQueryDTO,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: eventsKeys.calendar(query),
    queryFn: () => performanceApi.events.getCalendar(query),
    enabled: options?.enabled ?? true,
  });
}

export function useEventDateConflictsQuery(
  query: EventDateConflictQueryDTO | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: eventsKeys.dateConflicts(query ?? { eventDate: "" }),
    queryFn: () => performanceApi.events.checkDateConflicts(query!),
    enabled: (options?.enabled ?? true) && !!query?.eventDate,
    staleTime: 30_000,
  });
}

export function useEventTeamAssignmentWarningsCountQuery(options?: {
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: eventsKeys.teamAssignmentWarnings(),
    queryFn: () => performanceApi.events.countTeamAssignmentWarnings(),
    enabled: options?.enabled ?? true,
    refetchInterval: 60_000,
  });
}

export function useEventQuery(id: string) {
  return useQuery({
    queryKey: eventsKeys.detail(id),
    queryFn: () => performanceApi.events.get(id),
    enabled: !!id,
  });
}

export function useEventCalendarSyncQuery(id: string, enabled = true) {
  return useQuery({
    queryKey: eventsKeys.calendarSync(id),
    queryFn: () => performanceApi.events.googleCalendar(id),
    enabled: !!id && enabled,
  });
}

export function useResyncEventCalendar(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => performanceApi.events.syncGoogleCalendar(id),
    onSuccess: () => {
      toast.success("ສົ່ງຊິງຄ໌ Google Calendar ແລ້ວ");
      void qc.invalidateQueries({ queryKey: eventsKeys.calendarSync(id) });
    },
    onError: (error) => {
      toast.error(
        getPerformanceErrorMessage(error, "ຊິງຄ໌ Google Calendar ບໍ່ສຳເລັດ"),
      );
    },
  });
}

export function useEventEditQuery(id: string) {
  return useQuery({
    queryKey: eventsKeys.editForm(id),
    queryFn: () => performanceApi.events.getForEdit(id),
    enabled: !!id,
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEventDTO) => performanceApi.events.create(input),
    onSuccess: () => {
      toast.success("ສ້າງງານສະແດງສຳເລັດ");
      qc.invalidateQueries({ queryKey: eventsKeys.all });
    },
    onError: () => {
      toast.error("ສ້າງງານສະແດງລົ້ມເຫຼວ");
    },
  });
}

export function useUpdateEvent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateEventDTO) =>
      performanceApi.events.update(id, input),
    onSuccess: (data) => {
      toast.success("ອັບເດດງານສະແດງສຳເລັດ");
      qc.setQueryData(eventsKeys.editForm(id), (prev) =>
        prev
          ? {
              ...prev,
              ...data,
              lockedFeatures: prev.lockedFeatures,
            }
          : prev,
      );
      // Do not invalidate eventsKeys.all — list refetch + progress is slow.
      void qc.invalidateQueries({ queryKey: eventsKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: eventsKeys.lists() });
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ອັບເດດງານສະແດງລົ້ມເຫຼວ"));
    },
  });
}

export function useEventDateChangesQuery(eventId: string, enabled = true) {
  return useQuery({
    queryKey: eventsKeys.dateChanges(eventId),
    queryFn: () => performanceApi.events.dateChanges(eventId),
    enabled: !!eventId && enabled,
  });
}

export function usePostponeEvent(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PostponeEventDTO) =>
      performanceApi.events.postpone(eventId, input),
    onSuccess: () => {
      toast.success("ເລື່ອນວັນງານສຳເລັດ");
      qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
      qc.invalidateQueries({ queryKey: eventsKeys.dateChanges(eventId) });
      qc.invalidateQueries({ queryKey: eventsKeys.shows(eventId) });
      qc.invalidateQueries({ queryKey: eventsKeys.assignments(eventId) });
      qc.invalidateQueries({ queryKey: eventsKeys.lists() });
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ເລື່ອນວັນງານລົ້ມເຫຼວ"));
    },
  });
}

function invalidateEventShowQueries(
  qc: ReturnType<typeof useQueryClient>,
  eventId: string,
) {
  qc.invalidateQueries({ queryKey: eventsKeys.shows(eventId) });
  qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
  qc.invalidateQueries({ queryKey: eventsKeys.assignments(eventId) });
}

export function useEventShowsQuery(eventId: string, enabled = true) {
  return useQuery({
    queryKey: eventsKeys.shows(eventId),
    queryFn: () => performanceApi.events.shows.list(eventId),
    enabled: !!eventId && enabled,
  });
}

export function useCreateEventShow(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEventShowDTO) =>
      performanceApi.events.shows.create(eventId, input),
    onSuccess: () => {
      toast.success("ເພີ່ມໂຊສຳເລັດ");
      invalidateEventShowQueries(qc, eventId);
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ເພີ່ມໂຊລົ້ມເຫຼວ"));
    },
  });
}

export function useUpdateEventShow(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      showId,
      input,
    }: {
      showId: string;
      input: UpdateEventShowDTO;
    }) => performanceApi.events.shows.update(eventId, showId, input),
    onSuccess: () => {
      toast.success("ອັບເດດໂຊສຳເລັດ");
      invalidateEventShowQueries(qc, eventId);
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ອັບເດດໂຊລົ້ມເຫຼວ"));
    },
  });
}

export function useDeleteEventShow(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (showId: string) =>
      performanceApi.events.shows.remove(eventId, showId),
    onSuccess: () => {
      toast.success("ລຶບໂຊສຳເລັດ");
      invalidateEventShowQueries(qc, eventId);
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ລຶບໂຊລົ້ມເຫຼວ"));
    },
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => performanceApi.events.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: eventsKeys.all }),
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ລຶບງານສະແດງລົ້ມເຫຼວ")),
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

// ─── Assignments ─────────────────────────────────────────────────────────────────

export function useAssignmentsQuery(eventId: string) {
  return useQuery({
    queryKey: eventsKeys.assignments(eventId),
    queryFn: () => performanceApi.events.assignments.list(eventId),
    enabled: !!eventId,
  });
}

export function useRehearsalSummaryQuery(eventId: string, enabled: boolean) {
  return useQuery({
    queryKey: eventsKeys.rehearsalSummary(eventId),
    queryFn: () => performanceApi.events.rehearsalSummary(eventId),
    enabled: !!eventId && enabled,
  });
}

function invalidateRehearsalQueries(
  qc: ReturnType<typeof useQueryClient>,
  eventId: string,
) {
  qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
  qc.invalidateQueries({ queryKey: eventsKeys.assignments(eventId) });
  qc.invalidateQueries({ queryKey: eventsKeys.rehearsalSummary(eventId) });
  qc.invalidateQueries({ queryKey: eventsKeys.rehearsalSessions(eventId) });
}

export function useRehearsalSessionsQuery(eventId: string, enabled = true) {
  return useQuery({
    queryKey: eventsKeys.rehearsalSessions(eventId),
    queryFn: () => performanceApi.events.rehearsalSessions.list(eventId),
    enabled: !!eventId && enabled,
  });
}

export function useCreateRehearsalSession(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRehearsalSessionDTO) =>
      performanceApi.events.rehearsalSessions.create(eventId, input),
    onSuccess: () => {
      toast.success("ເພີ່ມວັນຊ້ອມສຳເລັດ");
      invalidateRehearsalQueries(qc, eventId);
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ເພີ່ມວັນຊ້ອມລົ້ມເຫຼວ"));
    },
  });
}

export type CreateRehearsalSessionsBulkPayload = {
  performerIds: string[];
  scheduledDates: string[];
  location: string | null;
  status?: CreateRehearsalSessionDTO["status"];
  notes: string | null;
  studioBooking?: CreateRehearsalSessionDTO["studioBooking"];
};

export function useCreateRehearsalSessionsBulk(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRehearsalSessionsBulkPayload) =>
      performanceApi.events.rehearsalSessions.bulkCreate(eventId, input),
    onSuccess: (rows) => {
      toast.success(`ເພີ່ມວັນຊ້ອມ ${rows.length} ລາຍການສຳເລັດ`);
      invalidateRehearsalQueries(qc, eventId);
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ເພີ່ມວັນຊ້ອມລົ້ມເຫຼວ"));
    },
  });
}

export function useBulkRescheduleRehearsalSessions(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkRescheduleRehearsalSessionsDTO) =>
      performanceApi.events.rehearsalSessions.bulkReschedule(eventId, input),
    onSuccess: (rows) => {
      toast.success(`ຍ້າຍວັນຊ້ອມ ${rows.length} ຄົນສຳເລັດ`);
      invalidateRehearsalQueries(qc, eventId);
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ຍ້າຍວັນຊ້ອມລົ້ມເຫຼວ"));
    },
  });
}

export function useBulkUpdateRehearsalSessionStatus(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkUpdateRehearsalSessionStatusDTO) =>
      performanceApi.events.rehearsalSessions.bulkUpdateStatus(eventId, input),
    onSuccess: (rows) => {
      toast.success(`ອັບເດດສະຖານະ ${rows.length} ຄົນສຳເລັດ`);
      invalidateRehearsalQueries(qc, eventId);
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ອັບເດດສະຖານະລົ້ມເຫຼວ"));
    },
  });
}

export function useUpdateRehearsalSession(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: UpdateRehearsalSessionDTO;
    }) =>
      performanceApi.events.rehearsalSessions.update(eventId, sessionId, input),
    onSuccess: () => {
      toast.success("ອັບເດດວັນຊ້ອມສຳເລັດ");
      invalidateRehearsalQueries(qc, eventId);
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ອັບເດດວັນຊ້ອມລົ້ມເຫຼວ"));
    },
  });
}

export function useDeleteRehearsalSession(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      performanceApi.events.rehearsalSessions.remove(eventId, sessionId),
    onSuccess: () => {
      toast.success("ລຶບວັນຊ້ອມສຳເລັດ");
      invalidateRehearsalQueries(qc, eventId);
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ລຶບວັນຊ້ອມລົ້ມເຫຼວ"));
    },
  });
}

export function useBulkCreateAssignments(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignments: CreateAssignmentDTO[]) =>
      performanceApi.events.assignments.bulkCreate(eventId, { assignments }),
    onSuccess: (result) => {
      const skipped = result.skipped.length;
      const created = result.created.length;
      if (skipped > 0) {
        toast.success(`ເພີ່ມ ${created} ລາຍການ (ຂ້າມ ${skipped} ລາຍການທີ່ຊ້ຳ)`);
      } else if (created > 0) {
        toast.success(`ເພີ່ມ ${created} ລາຍການສຳເລັດ`);
      } else {
        toast.error("ບໍ່ມີລາຍການໃໝ່ — ອາດມີການຈັດນັກສະແດງຄົນນີ້ໃນງານແລ້ວ");
      }
      qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
      qc.invalidateQueries({ queryKey: eventsKeys.assignments(eventId) });
      qc.invalidateQueries({
        queryKey: eventsKeys.rehearsalSummary(eventId),
      });
    },
    onError: (error) => {
      toast.error(getPerformanceErrorMessage(error, "ເພີ່ມ template ລົ້ມເຫຼວ"));
    },
  });
}

function invalidateAssignmentShowQueries(
  qc: ReturnType<typeof useQueryClient>,
  eventId: string,
) {
  qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
  qc.invalidateQueries({ queryKey: eventsKeys.assignments(eventId) });
  qc.invalidateQueries({
    queryKey: [...eventsKeys.assignments(eventId), "partial-show-participation"],
  });
  qc.invalidateQueries({ queryKey: eventsKeys.rehearsalSummary(eventId) });
}

export function useBulkUpdateAssignmentShows(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkUpdateAssignmentShowsDTO) =>
      performanceApi.events.assignments.bulkUpdateShows(eventId, input),
    onSuccess: (result) => {
      if (result.skipped.length > 0) {
        toast.success(
          `ອັບເດດ ${result.updated.length} ລາຍການ (ຂ້າມ ${result.skipped.length})`,
        );
      } else {
        toast.success("ອັບເດດໂຊສຳເລັດ");
      }
      invalidateAssignmentShowQueries(qc, eventId);
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ອັບເດດໂຊລົ້ມເຫຼວ")),
  });
}

export function useAddEventShowToAllAssignments(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddEventShowToAllAssignmentsDTO) =>
      performanceApi.events.assignments.addShowToAll(eventId, input),
    onSuccess: (result) => {
      toast.success(`ເພີ່ມໂຊໃຫ້ ${result.updated} ຄົນ`);
      invalidateAssignmentShowQueries(qc, eventId);
    },
    onError: (error) =>
      toast.error(
        getPerformanceErrorMessage(error, "ເພີ່ມໂຊໃຫ້ທຸກຄົນລົ້ມເຫຼວ"),
      ),
  });
}

export function usePartialShowParticipationQuery(
  eventId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: [...eventsKeys.assignments(eventId), "partial-show-participation"],
    queryFn: () =>
      performanceApi.events.assignments.partialShowParticipation(eventId),
    enabled: !!eventId && enabled,
  });
}

export function useEventDetailReportQuery(eventId: string, enabled = true) {
  return useQuery({
    queryKey: eventsKeys.report(eventId),
    queryFn: () => performanceApi.events.report(eventId),
    enabled: !!eventId && enabled,
  });
}

export function useUpdateEventFxRatesMutation(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateEventFxRatesDTO) =>
      performanceApi.events.updateFxRates(eventId, input),
    onSuccess: () => {
      toast.success("ບັນທຶກອັດຕາແລກປ່ຽນສຳເລັດ");
      void qc.invalidateQueries({ queryKey: eventsKeys.report(eventId) });
      void qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
    },
    onError: () => {
      toast.error("ບັນທຶກອັດຕາແລກປ່ຽນລົ້ມເຫຼວ");
    },
  });
}

export function useCreateAssignment(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAssignmentDTO) =>
      performanceApi.events.assignments.create(eventId, input),
    onSuccess: () => {
      toast.success("ເພີ່ມນັກສະແດງໃສ່ງານສຳເລັດ");
      qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
      qc.invalidateQueries({ queryKey: eventsKeys.teamAssignmentWarnings() });
      qc.invalidateQueries({ queryKey: eventsKeys.lists() });
      qc.invalidateQueries({
        queryKey: eventsKeys.rehearsalSummary(eventId),
      });
    },
    onError: () => {
      toast.error("ເພີ່ມນັກສະແດງໃສ່ງານລົ້ມເຫຼວ");
    },
  });
}

export function useUpdateAssignment(eventId: string, assignmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAssignmentDTO) =>
      performanceApi.events.assignments.update(eventId, assignmentId, input),
    onSuccess: () => {
      toast.success("ອັບເດດ assignment ສຳເລັດ");
      qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
      qc.invalidateQueries({ queryKey: eventsKeys.assignments(eventId) });
      qc.invalidateQueries({
        queryKey: eventsKeys.rehearsalSummary(eventId),
      });
    },
    onError: () => {
      toast.error("ອັບເດດ assignment ລົ້ມເຫຼວ");
    },
  });
}

export function useDeleteAssignment(eventId: string) {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (assignmentId: string) =>
      performanceApi.events.assignments.remove(eventId, assignmentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
      qc.invalidateQueries({ queryKey: eventsKeys.teamAssignmentWarnings() });
      qc.invalidateQueries({ queryKey: eventsKeys.lists() });
    },
    onError: () => toast.error("ລຶບ assignment ລົ້ມເຫຼວ"),
  });
  const run = (assignmentId: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(assignmentId, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

// --- Pricing Documents ------------------------------------------------------

export function useDocumentsQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: documentsKeys.list(),
    queryFn: () => performanceApi.documents.list(),
    enabled: options?.enabled ?? true,
    refetchInterval: 60_000,
  });
}

export function useDocumentQuery(documentId: string | null | undefined) {
  return useQuery({
    queryKey: documentId
      ? documentsKeys.detail(documentId)
      : [...documentsKeys.details(), "empty"],
    queryFn: () => performanceApi.documents.get(documentId ?? ""),
    enabled: !!documentId,
  });
}

export function useCreatePricingDraft(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input?: { currency?: "LAK" | "USD" | "THB" }) =>
      performanceApi.events.documents.createDraft(eventId, input ?? {}),
    onSuccess: (document) => {
      toast.success("ສ້າງເອກະສານຮ່າງລາຄາສຳເລັດ");
      qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
      qc.invalidateQueries({ queryKey: documentsKeys.detail(document.id) });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ສ້າງເອກະສານຮ່າງລາຄາບໍ່ສຳເລັດ")),
  });
}

export function useSyncPricingFromAssignments(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      performanceApi.events.documents.syncFromAssignments(eventId, documentId),
    onSuccess: (result) => {
      const warningSuffix =
        result.warnings.length > 0 ? ` (${result.warnings.length} ຄຳເຕືອນ)` : "";
      toast.success(`ຊິງກ໌ລາຄາສຳເລັດ${warningSuffix}`);
      qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
      qc.invalidateQueries({
        queryKey: documentsKeys.detail(result.document.id),
      });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ຊິງກ໌ລາຄາບໍ່ສຳເລັດ")),
  });
}

export function useUpdateDocumentCurrency(eventId: string, documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { currency: "LAK" | "USD" | "THB" }) =>
      performanceApi.events.documents.updateCurrency(
        eventId,
        documentId,
        input,
      ),
    onSuccess: (document) => {
      toast.success("ອັບເດດສະກຸນເງິນສຳເລັດ");
      qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
      qc.invalidateQueries({ queryKey: documentsKeys.detail(document.id) });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ອັບເດດສະກຸນເງິນບໍ່ສຳເລັດ")),
  });
}

export function useIssueDocument(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; issuedAt?: Date | string }) =>
      performanceApi.events.documents.issue(eventId, input.documentId, {
        ...(input.issuedAt ? { issuedAt: input.issuedAt } : {}),
      }),
    onSuccess: (document) => {
      toast.success("ອອກເອກະສານສຳເລັດ");
      qc.invalidateQueries({ queryKey: documentsKeys.lists() });
      qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
      qc.invalidateQueries({ queryKey: documentsKeys.detail(document.id) });
      if (document.parentDocumentId && document.documentType === "receipt") {
        qc.invalidateQueries({
          queryKey: eventsKeys.paymentSummary(
            eventId,
            document.parentDocumentId,
          ),
        });
      }
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ອອກເອກະສານບໍ່ສຳເລັດ")),
  });
}

export function useReopenDocument(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      performanceApi.events.documents.reopen(eventId, documentId),
    onSuccess: (document) => {
      toast.success("ຍ້ອນກັບເປັນຮ່າງແລ້ວ — ແກ້ໄຂໄດ້ອີກ");
      qc.invalidateQueries({ queryKey: documentsKeys.lists() });
      qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
      qc.invalidateQueries({ queryKey: documentsKeys.detail(document.id) });
      qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
      if (document.documentType === "invoice") {
        qc.invalidateQueries({
          queryKey: eventsKeys.paymentSummary(eventId, document.id),
        });
      }
      if (document.parentDocumentId && document.documentType === "receipt") {
        qc.invalidateQueries({
          queryKey: eventsKeys.paymentSummary(
            eventId,
            document.parentDocumentId,
          ),
        });
      }
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ຍ້ອນກັບສະຖານະບໍ່ສຳເລັດ")),
  });
}

export function useVoidDocument(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentId,
      reason,
    }: {
      documentId: string;
      reason?: string;
    }) => performanceApi.events.documents.void(eventId, documentId, { reason }),
    onSuccess: (document) => {
      toast.success("ຍົກເລີກເອກະສານສຳເລັດ");
      qc.invalidateQueries({ queryKey: documentsKeys.lists() });
      qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
      qc.invalidateQueries({ queryKey: documentsKeys.detail(document.id) });
      if (document.parentDocumentId && document.documentType === "receipt") {
        qc.invalidateQueries({
          queryKey: eventsKeys.paymentSummary(
            eventId,
            document.parentDocumentId,
          ),
        });
      }
      if (document.parentDocumentId && document.documentType === "invoice") {
        qc.invalidateQueries({
          queryKey: eventsKeys.quotationInstallmentPlan(
            eventId,
            document.parentDocumentId,
          ),
        });
      }
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ຍົກເລີກເອກະສານບໍ່ສຳເລັດ")),
  });
}

export function useDeleteDocument(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      performanceApi.events.documents.remove(eventId, documentId),
    onSuccess: (_result, documentId) => {
      toast.success("ລຶບເອກະສານສຳເລັດ");
      qc.invalidateQueries({ queryKey: documentsKeys.lists() });
      qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
      qc.removeQueries({ queryKey: documentsKeys.detail(documentId) });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ລຶບເອກະສານບໍ່ສຳເລັດ")),
  });
}

export function useCreateDocumentFromParent(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDocumentFromParentDTO) =>
      performanceApi.events.documents.createFromParent(eventId, input),
    onSuccess: (document) => {
      toast.success("ສ້າງເອກະສານຕໍ່ເນື່ອງສຳເລັດ");
      qc.invalidateQueries({ queryKey: documentsKeys.lists() });
      qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
      qc.invalidateQueries({ queryKey: documentsKeys.detail(document.id) });
      if (document.parentDocumentId) {
        qc.invalidateQueries({
          queryKey: eventsKeys.quotationInstallmentPlan(
            eventId,
            document.parentDocumentId,
          ),
        });
      }
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ສ້າງເອກະສານຕໍ່ເນື່ອງບໍ່ສຳເລັດ")),
  });
}

function invalidatePaymentQueries(
  qc: ReturnType<typeof useQueryClient>,
  eventId: string,
  invoiceId?: string | null,
) {
  qc.invalidateQueries({ queryKey: documentsKeys.lists() });
  qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
  if (invoiceId) {
    qc.invalidateQueries({
      queryKey: eventsKeys.paymentSummary(eventId, invoiceId),
    });
  }
}

export function useInvoicePaymentSummary(
  eventId: string,
  invoiceId: string | null | undefined,
) {
  return useQuery({
    queryKey: invoiceId
      ? eventsKeys.paymentSummary(eventId, invoiceId)
      : [...eventsKeys.detail(eventId), "payment-summary", "empty"],
    queryFn: () =>
      performanceApi.events.documents.getPaymentSummary(
        eventId,
        invoiceId ?? "",
      ),
    enabled: !!eventId && !!invoiceId,
  });
}

export function useQuotationInstallmentPlan(
  eventId: string,
  quotationId: string | null | undefined,
) {
  return useQuery({
    queryKey: quotationId
      ? eventsKeys.quotationInstallmentPlan(eventId, quotationId)
      : [...eventsKeys.detail(eventId), "quotation-installment-plan", "empty"],
    queryFn: () =>
      performanceApi.events.documents.getQuotationInstallmentPlan(
        eventId,
        quotationId ?? "",
      ),
    enabled: !!eventId && !!quotationId,
  });
}

export function useUpdateQuotationInstallmentPlan(
  eventId: string,
  quotationId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateInstallmentPlanDTO) =>
      performanceApi.events.documents.updateQuotationInstallmentPlan(
        eventId,
        quotationId,
        input,
      ),
    onSuccess: () => {
      toast.success("ບັນທຶກແຜນງວດສຳເລັດ");
      qc.invalidateQueries({
        queryKey: eventsKeys.quotationInstallmentPlan(eventId, quotationId),
      });
      qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ບັນທຶກແຜນງວດບໍ່ສຳເລັດ")),
  });
}

export function useCreatePaymentReceipt(eventId: string, invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePaymentReceiptDTO) =>
      performanceApi.events.documents.createPaymentReceipt(
        eventId,
        invoiceId,
        input,
      ),
    onSuccess: (document) => {
      toast.success("ສ້າງໃບຮັບເງິນຮ່າງສຳເລັດ");
      invalidatePaymentQueries(qc, eventId, invoiceId);
      qc.invalidateQueries({ queryKey: documentsKeys.detail(document.id) });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ສ້າງໃບຮັບເງິນບໍ່ສຳເລັດ")),
  });
}

export function useUpdatePaymentReceiptDraft(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentId,
      invoiceId,
      input,
    }: {
      documentId: string;
      invoiceId: string;
      input: UpdatePaymentReceiptDraftDTO;
    }) =>
      performanceApi.events.documents.updatePaymentReceiptDraft(
        eventId,
        documentId,
        input,
      ),
    onSuccess: (document, variables) => {
      toast.success("ບັນທຶກໃບຮັບເງິນສຳເລັດ");
      invalidatePaymentQueries(qc, eventId, variables.invoiceId);
      qc.invalidateQueries({ queryKey: documentsKeys.detail(document.id) });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ບັນທຶກໃບຮັບເງິນບໍ່ສຳເລັດ")),
  });
}

export function useUpdateInstallmentPlan(eventId: string, invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateInstallmentPlanDTO) =>
      performanceApi.events.documents.updateInstallmentPlan(
        eventId,
        invoiceId,
        input,
      ),
    onSuccess: () => {
      toast.success("ບັນທຶກແຜນງວດສຳເລັດ");
      invalidatePaymentQueries(qc, eventId, invoiceId);
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ບັນທຶກແຜນງວດບໍ່ສຳເລັດ")),
  });
}

export function useCreateManualLine(eventId: string, documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateManualLineDTO) =>
      performanceApi.documents.lines.create(documentId, input),
    onSuccess: () => {
      toast.success("ເພີ່ມລາຍການເອງສຳເລັດ");
      qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
      qc.invalidateQueries({ queryKey: documentsKeys.detail(documentId) });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ເພີ່ມລາຍການເອງບໍ່ສຳເລັດ")),
  });
}

export function useUpdateManualLine(eventId: string, documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      lineId,
      input,
    }: {
      lineId: string;
      input: UpdateManualLineDTO;
    }) => performanceApi.documents.lines.update(documentId, lineId, input),
    onSuccess: () => {
      toast.success("ແກ້ໄຂລາຍການເອງສຳເລັດ");
      qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
      qc.invalidateQueries({ queryKey: documentsKeys.detail(documentId) });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ແກ້ໄຂລາຍການເອງບໍ່ສຳເລັດ")),
  });
}

export function useDeleteManualLine(eventId: string, documentId: string) {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (lineId: string) =>
      performanceApi.documents.lines.remove(documentId, lineId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: eventsKeys.documents(eventId) });
      qc.invalidateQueries({ queryKey: documentsKeys.detail(documentId) });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ລຶບລາຍການເອງບໍ່ສຳເລັດ")),
  });
  const run = (lineId: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(lineId, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

// --- Performance Payouts ----------------------------------------------------

export function usePayoutsQuery() {
  return useQuery({
    queryKey: payoutsKeys.list(),
    queryFn: () => performanceApi.payouts.list(),
  });
}

export function useEventPayoutsQuery(eventId: string, enabled = true) {
  return useQuery({
    queryKey: eventsKeys.payouts(eventId),
    queryFn: () => performanceApi.events.payouts.list(eventId),
    enabled: !!eventId && enabled,
  });
}

export function useEventCommissionsQuery(eventId: string, enabled = true) {
  return useQuery({
    queryKey: eventsKeys.commissions(eventId),
    queryFn: () => performanceApi.events.commissions.list(eventId),
    enabled: !!eventId && enabled,
  });
}

function invalidateEventCommissions(
  qc: ReturnType<typeof useQueryClient>,
  eventId: string,
) {
  qc.invalidateQueries({ queryKey: eventsKeys.commissions(eventId) });
}

export function useCreateCommission(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCommissionDTO) =>
      performanceApi.events.commissions.create(eventId, input),
    onSuccess: () => {
      invalidateEventCommissions(qc, eventId);
      toast.success("ບັນທຶກຄ່ານາຍໜ້າສຳເລັດ");
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ບັນທຶກຄ່ານາຍໜ້າບໍ່ສຳເລັດ")),
  });
}

export function useUpdateCommission(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      commissionId,
      input,
    }: {
      commissionId: string;
      input: UpdateCommissionDTO;
    }) => performanceApi.events.commissions.update(eventId, commissionId, input),
    onSuccess: () => {
      invalidateEventCommissions(qc, eventId);
      toast.success("ອັບເດດຄ່ານາຍໜ້າສຳເລັດ");
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ອັບເດດຄ່ານາຍໜ້າບໍ່ສຳເລັດ")),
  });
}

export function useMarkCommissionPaid(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commissionId: string) =>
      performanceApi.events.commissions.markPaid(eventId, commissionId),
    onSuccess: () => {
      invalidateEventCommissions(qc, eventId);
      toast.success("ບັນທຶກຈ່າຍຄ່ານາຍໜ້າສຳເລັດ");
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ບັນທຶກຈ່າຍບໍ່ສຳເລັດ")),
  });
}

export function useCancelCommission(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commissionId: string) =>
      performanceApi.events.commissions.cancel(eventId, commissionId),
    onSuccess: () => {
      invalidateEventCommissions(qc, eventId);
      toast.success("ຍົກເລີກຄ່ານາຍໜ້າສຳເລັດ");
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ຍົກເລີກບໍ່ສຳເລັດ")),
  });
}

export function useDeleteCommission(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commissionId: string) =>
      performanceApi.events.commissions.remove(eventId, commissionId),
    onSuccess: () => {
      invalidateEventCommissions(qc, eventId);
      toast.success("ລຶບຄ່ານາຍໜ້າສຳເລັດ");
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ລຶບບໍ່ສຳເລັດ")),
  });
}

function invalidateEventProductionCosts(
  qc: ReturnType<typeof useQueryClient>,
  eventId: string,
) {
  qc.invalidateQueries({ queryKey: eventsKeys.productionCosts(eventId) });
  qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
}

export function useEventProductionCostsQuery(
  eventId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: eventsKeys.productionCosts(eventId),
    queryFn: () => performanceApi.events.productionCosts.list(eventId),
    enabled: !!eventId && enabled,
  });
}

export function useCreateProductionCostLine(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductionCostLineDTO) =>
      performanceApi.events.productionCosts.create(eventId, input),
    onSuccess: () => {
      invalidateEventProductionCosts(qc, eventId);
      toast.success("ບັນທຶກຕົ້ນທຸນສຳເລັດ");
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ບັນທຶກຕົ້ນທຸນບໍ່ສຳເລັດ")),
  });
}

export function useUpdateProductionCostLine(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      lineId,
      input,
    }: {
      lineId: string;
      input: UpdateProductionCostLineDTO;
    }) =>
      performanceApi.events.productionCosts.update(eventId, lineId, input),
    onSuccess: () => {
      invalidateEventProductionCosts(qc, eventId);
      toast.success("ອັບເດດຕົ້ນທຸນສຳເລັດ");
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ອັບເດດຕົ້ນທຸນບໍ່ສຳເລັດ")),
  });
}

export function useMarkProductionCostLinePaid(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineId: string) =>
      performanceApi.events.productionCosts.markPaid(eventId, lineId),
    onSuccess: () => {
      invalidateEventProductionCosts(qc, eventId);
      toast.success("ບັນທຶກຈ່າຍຕົ້ນທຸນສຳເລັດ");
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ບັນທຶກຈ່າຍບໍ່ສຳເລັດ")),
  });
}

export function useCancelProductionCostLine(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineId: string) =>
      performanceApi.events.productionCosts.cancel(eventId, lineId),
    onSuccess: () => {
      invalidateEventProductionCosts(qc, eventId);
      toast.success("ຍົກເລີກລາຍການສຳເລັດ");
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ຍົກເລີກບໍ່ສຳເລັດ")),
  });
}

export function useDeleteProductionCostLine(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineId: string) =>
      performanceApi.events.productionCosts.remove(eventId, lineId),
    onSuccess: () => {
      invalidateEventProductionCosts(qc, eventId);
      toast.success("ລຶບລາຍການສຳເລັດ");
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ລຶບບໍ່ສຳເລັດ")),
  });
}

function invalidateEventOutsources(
  qc: ReturnType<typeof useQueryClient>,
  eventId: string,
) {
  qc.invalidateQueries({ queryKey: eventsKeys.outsources(eventId) });
  qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
  qc.invalidateQueries({ queryKey: eventsKeys.payouts(eventId) });
  qc.invalidateQueries({ queryKey: eventsKeys.report(eventId) });
}

export function useEventOutsourcesQuery(eventId: string, enabled = true) {
  return useQuery({
    queryKey: eventsKeys.outsources(eventId),
    queryFn: () => performanceApi.events.outsources.list(eventId),
    enabled: !!eventId && enabled,
  });
}

export function useCreateEventOutsource(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEventOutsourceDTO) =>
      performanceApi.events.outsources.create(eventId, input),
    onSuccess: () => {
      invalidateEventOutsources(qc, eventId);
      toast.success("ເພີ່ມ Outsource ສຳເລັດ");
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ເພີ່ມ Outsource ບໍ່ສຳເລັດ")),
  });
}

export function useBulkCreateEventOutsourcesFromStaff(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkCreateEventOutsourcesFromStaffDTO) =>
      performanceApi.events.outsources.bulkCreateFromStaff(eventId, input),
    onSuccess: (result) => {
      invalidateEventOutsources(qc, eventId);
      const skipped = result.skipped.length;
      toast.success(
        skipped > 0
          ? `ເພີ່ມ Outsource ${result.created.length} ແຖວ (ຂ້າມ ${skipped})`
          : `ເພີ່ມ Outsource ${result.created.length} ແຖວ`,
      );
    },
    onError: (error) =>
      toast.error(
        getPerformanceErrorMessage(error, "ເພີ່ມ Outsource bulk ບໍ່ສຳເລັດ"),
      ),
  });
}

export function useUpdateEventOutsource(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      rowId,
      input,
    }: {
      rowId: string;
      input: UpdateEventOutsourceDTO;
    }) => performanceApi.events.outsources.update(eventId, rowId, input),
    onSuccess: () => {
      invalidateEventOutsources(qc, eventId);
      toast.success("ອັບເດດ Outsource ສຳເລັດ");
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ອັບເດດ Outsource ບໍ່ສຳເລັດ")),
  });
}

export function useDeleteEventOutsource(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rowId: string) =>
      performanceApi.events.outsources.remove(eventId, rowId),
    onSuccess: () => {
      invalidateEventOutsources(qc, eventId);
      toast.success("ລຶບ Outsource ສຳເລັດ");
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ລຶບ Outsource ບໍ່ສຳເລັດ")),
  });
}

export function useSaveEventOutsourceInstallmentPlan(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      rowId,
      input,
    }: {
      rowId: string;
      input: SaveEventOutsourceInstallmentPlanDTO;
    }) =>
      performanceApi.events.outsources.saveInstallmentPlan(
        eventId,
        rowId,
        input,
      ),
    onSuccess: () => {
      toast.success("ບັນທຶກແຜນງວດ AP ສຳເລັດ");
      invalidateEventOutsources(qc, eventId);
    },
    onError: (error) =>
      toast.error(
        getPerformanceErrorMessage(error, "ບັນທຶກແຜນງວດ AP ບໍ່ສຳເລັດ"),
      ),
  });
}

export function useOutsourceApInstallmentPlanTemplatesLookup() {
  return useQuery({
    queryKey: ["performance", "outsource-ap-installment-plan-templates", "lookup"],
    queryFn: () => performanceApi.outsourceApInstallmentPlanTemplates.lookup(),
  });
}

export function useCreateOutsourcePayout(eventId: string) {
  const qc = useQueryClient();
  const nav = useNavigate({ from: "/app/performance/events/$id" });
  return useMutation({
    mutationFn: ({
      rowId,
      input = {},
    }: {
      rowId: string;
      input?: CreateOutsourcePayoutDTO;
    }) => performanceApi.events.outsources.createPayout(eventId, rowId, input),
    onSuccess: (result) => {
      toast.success("ສ້າງໃບເບີກຈ່າຍ Outsource ສຳເລັດ");
      invalidateEventOutsources(qc, eventId);
      qc.invalidateQueries({ queryKey: payoutsKeys.lists() });
      qc.invalidateQueries({ queryKey: payoutsKeys.detail(result.payout.id) });
      nav({
        to: "/app/performance/payouts/$id",
        params: { id: result.payout.id },
      });
    },
    onError: (error) =>
      toast.error(
        getPerformanceErrorMessage(error, "ສ້າງໃບເບີກຈ່າຍ Outsource ບໍ່ສຳເລັດ"),
      ),
  });
}

export function useEventStaffQuery(
  eventId: string,
  options?: { activeOnly?: boolean; enabled?: boolean },
) {
  const queryEnabled = options?.enabled ?? true;
  return useQuery({
    queryKey: [...eventsKeys.staff(eventId), options?.activeOnly ?? "all"],
    queryFn: () =>
      performanceApi.events.staff.list(eventId, {
        activeOnly: options?.activeOnly,
      }),
    enabled: !!eventId && queryEnabled,
  });
}

function invalidateEventStaff(
  qc: ReturnType<typeof useQueryClient>,
  eventId: string,
) {
  qc.invalidateQueries({ queryKey: eventsKeys.staff(eventId) });
}

export function useAssignEventStaff(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignEventStaffDTO) =>
      performanceApi.events.staff.assign(eventId, input),
    onSuccess: () => {
      invalidateEventStaff(qc, eventId);
      toast.success("ມອບໝາຍ Staff ສຳເລັດ");
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ມອບໝາຍ Staff ບໍ່ສຳເລັດ")),
  });
}

export function useUpdateEventStaffAssignment(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      assignmentId,
      input,
    }: {
      assignmentId: string;
      input: UpdateEventStaffAssignmentDTO;
    }) => performanceApi.events.staff.update(eventId, assignmentId, input),
    onSuccess: () => {
      invalidateEventStaff(qc, eventId);
      toast.success("ອັບເດດການມອບໝາຍ Staff ສຳເລັດ");
    },
    onError: (error) =>
      toast.error(
        getPerformanceErrorMessage(error, "ອັບເດດການມອບໝາຍ Staff ບໍ່ສຳເລັດ"),
      ),
  });
}

export function useUnassignEventStaff(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      performanceApi.events.staff.remove(eventId, assignmentId),
    onSuccess: () => {
      invalidateEventStaff(qc, eventId);
      toast.success("ຍົກເລີກການມອບໝາຍ Staff ສຳເລັດ");
    },
    onError: (error) =>
      toast.error(
        getPerformanceErrorMessage(error, "ຍົກເລີກການມອບໝາຍ Staff ບໍ່ສຳເລັດ"),
      ),
  });
}

export function usePayoutQuery(payoutId: string | null | undefined) {
  return useQuery({
    queryKey: payoutId
      ? payoutsKeys.detail(payoutId)
      : [...payoutsKeys.details(), "empty"],
    queryFn: () => performanceApi.payouts.get(payoutId ?? ""),
    enabled: !!payoutId,
  });
}

export function usePayoutPricingInsightsQuery(
  payoutId: string | null | undefined,
) {
  return useQuery({
    queryKey: payoutId
      ? payoutsKeys.pricingInsights(payoutId)
      : [...payoutsKeys.all, "pricing-insights", "empty"],
    queryFn: () => performanceApi.payouts.pricingInsights(payoutId ?? ""),
    enabled: !!payoutId,
  });
}

export function useCreatePayout(eventId: string) {
  const qc = useQueryClient();
  const nav = useNavigate({ from: "/app/performance/events/$id" });
  return useMutation({
    mutationFn: () => performanceApi.events.payouts.create(eventId, {}),
    onSuccess: (result) => {
      const warningSuffix =
        result.warnings.length > 0 ? ` (${result.warnings.length} ຄຳເຕືອນ)` : "";
      toast.success(`ສ້າງໃບເບີກຈ່າຍສຳເລັດ${warningSuffix}`);
      qc.invalidateQueries({ queryKey: payoutsKeys.lists() });
      qc.invalidateQueries({ queryKey: eventsKeys.payouts(eventId) });
      qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
      qc.invalidateQueries({ queryKey: payoutsKeys.detail(result.payout.id) });
      nav({
        to: "/app/performance/payouts/$id",
        params: { id: result.payout.id },
      });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ສ້າງໃບເບີກຈ່າຍບໍ່ສຳເລັດ")),
  });
}

export function useCreateStaffPayout(eventId: string) {
  const qc = useQueryClient();
  const nav = useNavigate({ from: "/app/performance/events/$id" });
  return useMutation({
    mutationFn: (input: CreateStaffPayoutDTO) =>
      performanceApi.events.payouts.createStaff(eventId, input),
    onSuccess: (result) => {
      toast.success("ສ້າງໃບເບີກຈ່າຍ Staff ສຳເລັດ");
      qc.invalidateQueries({ queryKey: payoutsKeys.lists() });
      qc.invalidateQueries({ queryKey: eventsKeys.payouts(eventId) });
      qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
      qc.invalidateQueries({ queryKey: payoutsKeys.detail(result.payout.id) });
      nav({
        to: "/app/performance/payouts/$id",
        params: { id: result.payout.id },
      });
    },
    onError: (error) =>
      toast.error(
        getPerformanceErrorMessage(error, "ສ້າງໃບເບີກຈ່າຍ Staff ບໍ່ສຳເລັດ"),
      ),
  });
}

export function useCreateProductionPayout(eventId: string) {
  const qc = useQueryClient();
  const nav = useNavigate({ from: "/app/performance/events/$id" });
  return useMutation({
    mutationFn: (input: CreateProductionPayoutDTO) =>
      performanceApi.events.payouts.createProduction(eventId, input),
    onSuccess: (result) => {
      toast.success("ສ້າງໃບເບີກຈ່າຍ Prop / ວັດສະດຸ ສຳເລັດ");
      qc.invalidateQueries({ queryKey: payoutsKeys.lists() });
      qc.invalidateQueries({ queryKey: eventsKeys.payouts(eventId) });
      qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
      qc.invalidateQueries({ queryKey: eventsKeys.productionCosts(eventId) });
      qc.invalidateQueries({ queryKey: payoutsKeys.detail(result.payout.id) });
      nav({
        to: "/app/performance/payouts/$id",
        params: { id: result.payout.id },
      });
    },
    onError: (error) =>
      toast.error(
        getPerformanceErrorMessage(
          error,
          "ສ້າງໃບເບີກຈ່າຍ Prop / ວັດສະດຸ ບໍ່ສຳເລັດ",
        ),
      ),
  });
}

export function useUpdatePayout(payoutId: string, eventId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePayoutDTO) =>
      performanceApi.payouts.update(payoutId, input),
    onSuccess: (payout) => {
      toast.success("ບັນທຶກໃບເບີກຈ່າຍສຳເລັດ");
      qc.invalidateQueries({ queryKey: payoutsKeys.lists() });
      qc.invalidateQueries({ queryKey: payoutsKeys.detail(payout.id) });
      qc.invalidateQueries({ queryKey: eventsKeys.payouts(payout.eventId) });
      if (eventId)
        qc.invalidateQueries({ queryKey: eventsKeys.payouts(eventId) });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ບັນທຶກໃບເບີກຈ່າຍບໍ່ສຳເລັດ")),
  });
}

function invalidatePayoutQueries(
  qc: ReturnType<typeof useQueryClient>,
  payout: { id: string; eventId: string },
) {
  qc.invalidateQueries({ queryKey: payoutsKeys.lists() });
  qc.invalidateQueries({ queryKey: payoutsKeys.detail(payout.id) });
  qc.invalidateQueries({ queryKey: eventsKeys.payouts(payout.eventId) });
  qc.invalidateQueries({ queryKey: eventsKeys.productionCosts(payout.eventId) });
}

export function useSubmitPayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payoutId: string) => performanceApi.payouts.submit(payoutId),
    onSuccess: (payout) => {
      toast.success("ສົ່ງອະນຸມັດສຳເລັດ");
      invalidatePayoutQueries(qc, payout);
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ສົ່ງອະນຸມັດບໍ່ສຳເລັດ")),
  });
}

export function useApprovePayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payoutId: string) => performanceApi.payouts.approve(payoutId),
    onSuccess: (payout) => {
      toast.success("ອະນຸມັດໃບເບີກຈ່າຍສຳເລັດ");
      invalidatePayoutQueries(qc, payout);
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ອະນຸມັດໃບເບີກຈ່າຍບໍ່ສຳເລັດ")),
  });
}

export function useRejectPayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payoutId: string) => performanceApi.payouts.reject(payoutId),
    onSuccess: (payout) => {
      toast.success("ສົ່ງກັບໄປແກ້ໄຂສຳເລັດ");
      invalidatePayoutQueries(qc, payout);
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ສົ່ງກັບໄປແກ້ໄຂບໍ່ສຳເລັດ")),
  });
}

export function useMarkPayoutPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      payoutId,
      input,
    }: {
      payoutId: string;
      input?: MarkPayoutPaidDTO;
    }) => performanceApi.payouts.markPaid(payoutId, input),
    onSuccess: (payout) => {
      toast.success("ບັນທຶກຈ່າຍແລ້ວສຳເລັດ");
      invalidatePayoutQueries(qc, payout);
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ບັນທຶກຈ່າຍແລ້ວບໍ່ສຳເລັດ")),
  });
}

export function useSetPayoutPaymentProof(payoutId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SetPayoutPaymentProofDTO) =>
      performanceApi.payouts.setPaymentProof(payoutId, input),
    onSuccess: (payout) => {
      toast.success("ບັນທຶກຫຼັກຖານການຈ່າຍສຳເລັດ");
      invalidatePayoutQueries(qc, payout);
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ບັນທຶກຫຼັກຖານການຈ່າຍບໍ່ສຳເລັດ")),
  });
}

export function useCancelPayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ payoutId, reason }: { payoutId: string; reason?: string }) =>
      performanceApi.payouts.cancel(payoutId, { reason }),
    onSuccess: (payout) => {
      toast.success("ຍົກເລີກໃບເບີກຈ່າຍສຳເລັດ");
      invalidatePayoutQueries(qc, payout);
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ຍົກເລີກໃບເບີກຈ່າຍບໍ່ສຳເລັດ")),
  });
}

export function useDeletePayout(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payoutId: string) =>
      performanceApi.events.payouts.remove(eventId, payoutId),
    onSuccess: (_result, payoutId) => {
      toast.success("ລຶບໃບເບີກຈ່າຍສຳເລັດ");
      qc.invalidateQueries({ queryKey: payoutsKeys.lists() });
      qc.invalidateQueries({ queryKey: eventsKeys.payouts(eventId) });
      qc.removeQueries({ queryKey: payoutsKeys.detail(payoutId) });
    },
    onError: (error) =>
      toast.error(getPerformanceErrorMessage(error, "ລຶບໃບເບີກຈ່າຍບໍ່ສຳເລັດ")),
  });
}

export const performanceReportsKeys = {
  all: ["performance-reports"] as const,
  summary: (query: PerformanceReportSummaryQueryDTO) =>
    [...performanceReportsKeys.all, "summary", query] as const,
  events: (query: PerformanceEventReportQueryDTO) =>
    [...performanceReportsKeys.all, "events", query] as const,
  payouts: (query: PerformancePayoutReportQueryDTO) =>
    [...performanceReportsKeys.all, "payouts", query] as const,
  revenue: (query: PerformanceRevenueReportQueryDTO) =>
    [...performanceReportsKeys.all, "revenue", query] as const,
  requests: (query: PerformanceRequestReportQueryDTO) =>
    [...performanceReportsKeys.all, "requests", query] as const,
  performers: (query: PerformancePerformerReportQueryDTO) =>
    [...performanceReportsKeys.all, "performers", query] as const,
};

export function usePerformanceReportSummaryQuery(
  query: PerformanceReportSummaryQueryDTO,
) {
  return useQuery({
    queryKey: performanceReportsKeys.summary(query),
    queryFn: () => performanceApi.reports.getSummary(query),
  });
}

export function usePerformanceEventReportQuery(
  query: PerformanceEventReportQueryDTO,
) {
  return useQuery({
    queryKey: performanceReportsKeys.events(query),
    queryFn: () => performanceApi.reports.getEvents(query),
  });
}

export function usePerformancePayoutReportQuery(
  query: PerformancePayoutReportQueryDTO,
) {
  return useQuery({
    queryKey: performanceReportsKeys.payouts(query),
    queryFn: () => performanceApi.reports.getPayouts(query),
  });
}

export function usePerformanceRevenueReportQuery(
  query: PerformanceRevenueReportQueryDTO,
) {
  return useQuery({
    queryKey: performanceReportsKeys.revenue(query),
    queryFn: () => performanceApi.reports.getRevenue(query),
  });
}

export function usePerformanceRequestReportQuery(
  query: PerformanceRequestReportQueryDTO,
) {
  return useQuery({
    queryKey: performanceReportsKeys.requests(query),
    queryFn: () => performanceApi.reports.getRequests(query),
  });
}

export function usePerformancePerformerReportQuery(
  query: PerformancePerformerReportQueryDTO,
) {
  return useQuery({
    queryKey: performanceReportsKeys.performers(query),
    queryFn: () => performanceApi.reports.getPerformers(query),
  });
}

export const performanceDashboardKeys = {
  all: ["performance-dashboard"] as const,
  operations: () => [...performanceDashboardKeys.all, "operations"] as const,
};

export function usePerformanceOperationsDashboardQuery() {
  return useQuery({
    queryKey: performanceDashboardKeys.operations(),
    queryFn: () => performanceApi.dashboard.get(),
  });
}
